import { MockPricePoint } from "@/lib/mockData";

export interface PriceChartData {
  currentPrice: number;
  pairLabel: string;
  priceChange24h: number;
  priceUsd: number;
  history: MockPricePoint[];
}

interface ChartPadding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface PriceChartGridLine {
  y: number;
  label: string;
}

export interface PriceChartViewModel {
  areaPath: string;
  gridLines: PriceChartGridLine[];
  isPositive: boolean;
  padding: ChartPadding;
  path: string;
  strokeColor: string;
  svgHeight: number;
  svgWidth: number;
}

const DESKTOP_PADDING: ChartPadding = {
  top: 20,
  right: 65,
  bottom: 5,
  left: 10,
};

const COMPACT_PADDING: ChartPadding = {
  top: 20,
  right: 10,
  bottom: 10,
  left: 10,
};

export const DESKTOP_TIME_LABELS = [
  "18:00",
  "22:00",
  "0:00",
  "2:00",
  "4:00",
  "6:00",
  "8:00",
  "10:00",
  "12:00",
  "14:00",
  "16:00",
  "18:00",
];

function getGridStep(range: number): number {
  if (range > 3) return 1;
  if (range > 1) return 0.5;
  return 0.1;
}

export function buildPriceChartViewModel(
  data: PriceChartData,
  compact: boolean,
): PriceChartViewModel {
  const svgWidth = compact ? 400 : 800;
  const svgHeight = compact ? 120 : 380;
  const padding = compact ? COMPACT_PADDING : DESKTOP_PADDING;

  const prices = data.history.map((point) => point.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;
  const chartWidth = svgWidth - padding.left - padding.right;
  const chartHeight = svgHeight - padding.top - padding.bottom;

  const points = data.history.map((point, index) => {
    const x = padding.left + (index / (data.history.length - 1)) * chartWidth;
    const y = padding.top + (1 - (point.price - min) / range) * chartHeight;
    return { x, y };
  });

  const path = points
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`)
    .join(" ");

  const areaPath =
    path +
    ` L ${points[points.length - 1].x.toFixed(1)} ${svgHeight - padding.bottom}` +
    ` L ${points[0].x.toFixed(1)} ${svgHeight - padding.bottom} Z`;

  const gridStep = getGridStep(range);
  const gridMin = Math.ceil(min / gridStep) * gridStep;
  const gridLines: PriceChartGridLine[] = [];

  for (let value = gridMin; value <= max; value += gridStep) {
    const y = padding.top + (1 - (value - min) / range) * chartHeight;
    gridLines.push({ y, label: `$${value.toFixed(2)}` });
  }

  const isPositive = data.priceChange24h >= 0;

  return {
    areaPath,
    gridLines,
    isPositive,
    padding,
    path,
    strokeColor: isPositive
      ? "var(--color-sr-lemon-muted)"
      : "var(--color-sr-salmon)",
    svgHeight,
    svgWidth,
  };
}
